import { VEHICLE_TYPES } from './icons'
import { hasIconSize } from './objectMeta'
import type { DisplayUnits, ObjectSize, ScenarioObject, TimelineAction } from './types'
import { TYPE_COLORS, defaultRadiusM, zoneCfgFor } from './toolUi'

/** Beat-side props authors may set via `setParams`. */
export type BeatEffectProp = 'color' | 'size' | 'radius'

export type BeatColorEffect = {
  prop: 'color'
  objectId: string
  color: string
}

export type BeatSizeEffect = {
  prop: 'size'
  objectId: string
  size: ObjectSize
}

export type BeatRadiusEffect = {
  prop: 'radius'
  objectId: string
  /** Meters — same storage as `params.radiusM`. */
  radiusM: number
}

export type BeatEffect = BeatColorEffect | BeatSizeEffect | BeatRadiusEffect

/** Swatches for beat color effects — same set as Type/Label. */
export const BEAT_COLOR_SWATCHES: readonly string[] = TYPE_COLORS

/** Default when adding a color effect (warm yellow — matches Hudson bird-strike step). */
export const BEAT_COLOR_DEFAULT = '#FAC775'

export const BEAT_SIZE_OPTIONS: readonly ObjectSize[] = ['sm', 'md', 'lg', 'xl']

/** Default size step when adding a size effect. */
export const BEAT_SIZE_DEFAULT: ObjectSize = 'lg'

/** Compact chip labels (≤2 chars) for summary rows. */
export const BEAT_SIZE_CHIP: Record<ObjectSize, string> = {
  sm: 'SM',
  md: 'MD',
  lg: 'LG',
  xl: 'XL',
}

/** Seg control labels — keep Medium short so four options fit. */
export const BEAT_SIZE_BUTTON: Record<ObjectSize, string> = {
  sm: 'Small',
  md: 'Med',
  lg: 'Large',
  xl: 'XL',
}

export const BEAT_PROP_LABEL: Record<BeatEffectProp, string> = {
  color: 'Color',
  size: 'Size',
  radius: 'Radius',
}

export function isObjectSize(value: unknown): value is ObjectSize {
  return value === 'sm' || value === 'md' || value === 'lg' || value === 'xl'
}

/**
 * Types whose map markers honor `params.color` overrides from beat `setParams`.
 * Vehicles first (Hudson airplane); expand as more markers paint param color.
 */
export function objectAcceptsBeatColor(type: string): boolean {
  return VEHICLE_TYPES.has(type)
}

/** Types whose map markers honor icon size (same gate as Inspect Size). */
export function objectAcceptsBeatSize(type: string): boolean {
  return hasIconSize(type)
}

/**
 * Same gate as Inspect footprint/radius control: zone cfg and/or numeric radiusM.
 * Tornado excluded (Inspect hides the radius field for it).
 */
export function objectAcceptsBeatRadius(obj: ScenarioObject): boolean {
  if (obj.type === 'tornado' || obj.type === 'hurricane') return false
  if (zoneCfgFor(obj.type)) return true
  return typeof obj.params?.radiusM === 'number' && Number.isFinite(obj.params.radiusM)
}

export function defaultBeatRadiusM(obj: ScenarioObject): number {
  const raw = Number(obj.params?.radiusM)
  if (Number.isFinite(raw) && raw > 0) return raw
  return defaultRadiusM(obj.type) ?? 100
}

export function beatPropsForObject(obj: ScenarioObject): BeatEffectProp[] {
  const props: BeatEffectProp[] = []
  if (objectAcceptsBeatColor(obj.type)) props.push('color')
  if (objectAcceptsBeatSize(obj.type)) props.push('size')
  if (objectAcceptsBeatRadius(obj)) props.push('radius')
  return props
}

/** Objects that accept at least one beat effect prop. */
export function beatEffectTargets(objects: readonly ScenarioObject[]): ScenarioObject[] {
  return objects.filter((o) => !o.ignored && beatPropsForObject(o).length > 0)
}

export function effectsFromActions(actions: readonly TimelineAction[]): BeatEffect[] {
  const rows: BeatEffect[] = []
  for (const a of actions ?? []) {
    if (a.kind !== 'setParams') continue
    const rawColor = a.params?.color
    if (typeof rawColor === 'string' && rawColor.trim()) {
      rows.push({ prop: 'color', objectId: a.objectId, color: rawColor.trim() })
    }
    const rawSize = a.params?.size
    if (isObjectSize(rawSize)) {
      rows.push({ prop: 'size', objectId: a.objectId, size: rawSize })
    }
    const rawRadius = a.params?.radiusM
    if (typeof rawRadius === 'number' && Number.isFinite(rawRadius) && rawRadius > 0) {
      rows.push({ prop: 'radius', objectId: a.objectId, radiusM: rawRadius })
    }
  }
  return rows
}

const BEAT_PARAM_KEYS = new Set<string>(['color', 'size', 'radiusM'])

/**
 * Replace beat-owned `setParams` keys; keep reveals and other keys.
 */
export function applyEffectsToActions(
  actions: readonly TimelineAction[],
  rows: readonly BeatEffect[]
): TimelineAction[] {
  const kept: TimelineAction[] = []
  for (const a of actions ?? []) {
    if (a.kind === 'reveal') {
      kept.push(a)
      continue
    }
    if (a.kind === 'setParams') {
      const rest: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(a.params ?? {})) {
        if (!BEAT_PARAM_KEYS.has(k)) rest[k] = v
      }
      if (Object.keys(rest).length > 0) {
        kept.push({ kind: 'setParams', objectId: a.objectId, params: rest })
      }
    }
  }
  for (const row of rows) {
    const id = row.objectId.trim()
    if (!id) continue
    if (row.prop === 'color') {
      const color = row.color.trim()
      if (!color) continue
      kept.push({ kind: 'setParams', objectId: id, params: { color } })
    } else if (row.prop === 'size') {
      kept.push({ kind: 'setParams', objectId: id, params: { size: row.size } })
    } else {
      if (!(row.radiusM > 0) || !Number.isFinite(row.radiusM)) continue
      kept.push({ kind: 'setParams', objectId: id, params: { radiusM: row.radiusM } })
    }
  }
  return kept
}

export function targetLabel(obj: ScenarioObject): string {
  const name = obj.label?.trim()
  return name || obj.type
}

export function effectSummaryLabel(effect: BeatEffect, objects: readonly ScenarioObject[]): string {
  const obj = objects.find((o) => o.id === effect.objectId)
  const name = obj ? targetLabel(obj) : 'Missing object'
  // Value lives in the leading chip/swatch — don't repeat it in the text.
  return `${BEAT_PROP_LABEL[effect.prop]} · ${name}`
}

/** Compact chip for radius summaries (display units). */
export function radiusEffectChip(radiusM: number, units: DisplayUnits): string {
  if (units === 'imperial') {
    const ft = Math.round(radiusM * 3.28084)
    if (ft >= 5280) {
      const mi = ft / 5280
      return Math.abs(mi - Math.round(mi)) < 0.05 ? `${Math.round(mi)}mi` : `${mi.toFixed(1)}mi`
    }
    return `${ft}ft`
  }
  return `${Math.round(radiusM)}m`
}
